// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * TypeScript types for Letter of Authority (LOA) functionality
 */

import type { ProviderStatusSummaryDTO } from './providerStatus';

export enum LOAStatus {
  DRAFT = 'DRAFT',
  GENERATING = 'GENERATING',
  PENDING_SIGNATURE = 'PENDING_SIGNATURE',
  PARTIALLY_SIGNED = 'PARTIALLY_SIGNED',
  COMPLETED = 'COMPLETED',
  DECLINED = 'DECLINED',
  FAILED = 'FAILED',
  EXPIRED = 'EXPIRED',
  VOIDED = 'VOIDED'
}

export enum SigningMethod {
  EMBEDDED = 'EMBEDDED',
  EMAIL = 'EMAIL'
}

export enum SignatoryStatus {
  PENDING = 'PENDING',
  VIEWED = 'VIEWED',
  SIGNED = 'SIGNED',
  DECLINED = 'DECLINED'
}

export interface SignatoryDTO {
  clientId: string;
  signatoryEmail: string;
  signatoryName: string;
  signingOrder: number;
  status?: SignatoryStatus;
  viewedAt?: string;
  signedAt?: string;
  declinedAt?: string;
  declineReason?: string;
  signingUrl?: string;
}

export interface LOADTO {
  id: string;
  /** ProfileRequest id this LOA is linked to (V112 FK). Lets callers route
   * subsequent operations through the unified /api/requests/{id}/* surface. */
  requestId?: string;
  brokerageId: string;
  profileId: string;
  policyIds: string[];
  status: LOAStatus;
  documentUrl?: string;
  documentStorageUrl?: string;
  signedDocumentUrl?: string;
  docusignEnvelopeId?: string;
  signingMethod: SigningMethod;
  sentForSigningAt?: string;
  completedAt?: string;
  expiresAt?: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
  signatories: SignatoryDTO[];
  providerStatusSummary?: ProviderStatusSummaryDTO;
}

export interface LOACreateRequest {
  profileId: string;
  policyIds: string[];
  // Optional: the BFF-resolved X-Brokerage-Id header is the authoritative
  // tenant identifier. Kept for backward compatibility; callers that set it
  // explicitly win only when the header is absent.
  brokerageId?: string;
  /** @deprecated Always EMBEDDED on the backend; field ignored. */
  signingMethod?: SigningMethod;
  /** @deprecated Signatories are extracted from policies on the backend. */
  signatories?: SignatoryDTO[];
  /** @deprecated LOAs are always sent immediately on the backend. */
  sendImmediately?: boolean;
}

export interface LOASendForSigningRequest {
  signingMethod: SigningMethod;
}

export interface EmbeddedSigningUrlResponse {
  signingUrl: string;
  expiresIn: number;
  returnUrl?: string;
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

/**
 * Phase 4: Provider Delivery Status Types
 */
export interface ProviderEmailStatusDTO {
  providerId: string;
  providerName: string;
  emailAddress: string;
  sent: number;
  failed: number;
  pending: number;
  retryScheduled: number;
  lastSentAt?: string;
  lastFailedAt?: string;
}

export interface ProviderDeliveryStatusDTO {
  loaId: string;
  totalProviders: number;
  providerStatuses: ProviderEmailStatusDTO[];
}
